import React, { useEffect, useMemo, useState } from "react";
import { PageLoader } from "../../../Components/Loader/PageLoader";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Grid,
  Box,
  Button,
  TextField,
  InputAdornment,
  Typography,
  alpha,
  Checkbox,
} from "@mui/material";
import useBulkSelection from "../../../hooks/useBulkSelection";
import BulkActionBar from "../../../Components/common/BulkActionBar";
import {
  useDeleteProductTagMutation,
  useGetProductTagQuery,
} from "../../../Api/attoDeskApi";
import { IProductTag } from "../../../Api/Interface/api.interface";
import DeletePopup from "../../../Components/Delete/DeletePopup";
import { useNotifier } from "../../../Core/Notifier";
import useDebounce from "../../../Components/SearchDebonce/useDebounce";
import { CiSearch } from "react-icons/ci";
import { useNavigate } from "react-router-dom";
import { useStyles } from "../../../theme/appStyles";
import ProductTag from "../Edit/index";
import CustomCheckbox from "../../../Components/Checkbox.tsx";
import Pagination from "../../../Components/Pagination";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import PageContainer from "../../../Components/layout/PageContainer";
import ActionButtons from "../../../Components/ui/ActionButtons";
import { NAVY, NAVY_DARK, GOLD, GOLD_SOFT, BORDER, TEXT_PRIMARY, TEXT_SECONDARY, SUCCESS, ERROR as RED } from "../../../theme/tokens";

const Index = () => {
  const classes = useStyles();
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();
  const [searchQuery, setSearchQuery] = useState("");
  const [currentSearchQuery, setCurrentSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [selectedCoursing, setSelectedCoursing] = useState<IProductTag | null>(
    null
  );
  const debouncedSearchQuery = useDebounce(searchQuery, 300);
  const [openDialog, setOpenDialog] = useState(false);
  const [openDeleteCategory, setOpenDeleteCategory] = useState(false);
  const [coursingToDelete, setCoursingToDelete] = useState<IProductTag | null>(
    null
  );

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const { data, isLoading, isError, refetch } = useGetProductTagQuery({
    searchText: currentSearchQuery,
    isActive: null,
  });

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (debouncedSearchQuery.length >= 4 || debouncedSearchQuery === "") {
      setCurrentSearchQuery(debouncedSearchQuery);
      refetch();
    }
  }, [debouncedSearchQuery]);

  const handleOpenDialog = (coursing: IProductTag) => {
    setSelectedCoursing(coursing);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setSelectedCoursing(null);
    setOpenDialog(false);
  };

  const handleOpenDeletePopup = (coursing: IProductTag) => {
    setCoursingToDelete(coursing);
    setOpenDeleteCategory(true);
  };

  const handleCloseDeletePopup = () => {
    setCoursingToDelete(null);
    setOpenDeleteCategory(false);
  };

  const [deleteCategory] = useDeleteProductTagMutation();
  const handleDelete = async (id: string) => {
    try {
      const response = await deleteCategory(id).unwrap();
      if (response.status) {
        showMessage("Deleted successfully");
        setOpenDeleteCategory(false);
      } else {
        showErrorMessage("Failed to delete the product tag");
      }
    } catch (error) {
      showErrorMessage("Failed to delete the product tag");
    }
  };

  const coursings: IProductTag[] = Array.isArray(data?.data)
    ? (data?.data as IProductTag[])
    : [];

  const bulk = useBulkSelection<IProductTag>(coursings);
  const API_URL = process.env.REACT_APP_API_URL;

  if (isLoading) return <PageLoader height="10vh" />;
  const handleBulkDelete = async () => {
    if (!bulk.selectedIds.length) return;
    if (!window.confirm(`Delete ${bulk.selectedIds.length} selected tags?`)) return;
    try {
      const token = localStorage.getItem("token") || "";
      const res = await fetch(`${API_URL}/product/tags/bulk-delete`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ ids: bulk.selectedIds }),
      });
      const json = await res.json();
      if (json.status) {
        showMessage(json.message || "Tags deleted");
        bulk.clearSelection();
        refetch();
      } else {
        showErrorMessage(json.message || "Bulk delete failed");
      }
    } catch (err) {
      showErrorMessage("Bulk delete failed");
    }
  };

  const handleSearch = () => {
    setCurrentSearchQuery(searchQuery);
  };

  const resetFields = () => {
    setSearchQuery("");
    setCurrentSearchQuery("");
    refetch();
  };

  const handleAddProductClick = () => {
    navigate("/tag/create");
  };

  return (
    <PageContainer disablePaper>
      {/* ═══ HEADER ═══ */}
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 2, mb: 2.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{
            width: 44, height: 44, borderRadius: 3, display: "flex", alignItems: "center", justifyContent: "center",
            background: `linear-gradient(135deg, ${NAVY} 0%, ${NAVY_DARK} 100%)`,
            boxShadow: `0 6px 18px ${alpha(NAVY, 0.35)}`,
            border: `1px solid ${alpha(GOLD, 0.35)}`,
          }}>
            <LocalOfferIcon sx={{ fontSize: 22, color: GOLD }} />
          </Box>
          <Box>
            <Typography fontSize={22} fontWeight={800} color={NAVY} letterSpacing="-0.02em">Product Tags</Typography>
            <Typography fontSize={12} color={TEXT_SECONDARY}>{coursings.length} tags</Typography>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 1, alignItems: "center", flexWrap: "wrap" }}>
          <TextField
            size="small"
            placeholder="Tag Name..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CiSearch style={{ color: TEXT_SECONDARY }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: 220,
              "& .MuiOutlinedInput-root": {
                borderRadius: 2.5, fontSize: 13, bgcolor: "#fff",
                "& fieldset": { borderColor: BORDER },
                "&:hover fieldset": { borderColor: alpha(GOLD, 0.6) },
                "&.Mui-focused fieldset": { borderColor: GOLD, borderWidth: "1.5px" },
              },
            }}
          />

          <Button
            variant="contained"
            color="secondary"
            size="small"
            disableElevation
            startIcon={<AddIcon sx={{ fontSize: 16 }} />}
            onClick={handleAddProductClick}
            sx={{
              borderRadius: 2.5, textTransform: "none", fontWeight: 700, fontSize: 12, px: 2.5,
              color: NAVY_DARK,
              background: `linear-gradient(135deg, ${GOLD} 0%, ${GOLD_SOFT} 100%)`,
              boxShadow: `0 4px 14px ${alpha(GOLD, 0.45)}`,
              "&:hover": {
                background: `linear-gradient(135deg, #C8A030 0%, ${GOLD} 100%)`,
                boxShadow: `0 6px 20px ${alpha(GOLD, 0.55)}`,
                transform: "translateY(-1px)",
              },
            }}
          >
            Add Tag
          </Button>
        </Box>
      </Box>

      <BulkActionBar
        selectedCount={bulk.selectedIds.length}
        onClear={bulk.clearSelection}
        onDelete={handleBulkDelete}
      />

      {/* ═══ TABLE ═══ */}
      <Box sx={{ bgcolor: "#fff", borderRadius: "12px", border: `1px solid ${BORDER}`, overflow: "hidden", boxShadow: "0 4px 12px rgba(11,30,45,0.06)" }}>
        <TableContainer sx={{ maxHeight: "calc(100vh - 240px)", scrollBehavior: "smooth" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 40, py: 1.5 }}>
                  <Checkbox
                    size="small"
                    checked={bulk.allSelected}
                    indeterminate={bulk.indeterminate}
                    onChange={() => bulk.toggleAll(coursings)}
                  />
                </TableCell>
                <TableCell sx={{ py: 1.5 }}>#</TableCell>
                <TableCell sx={{ py: 1.5 }}>Tag Name</TableCell>
                <TableCell sx={{ py: 1.5 }}>Active</TableCell>
                <TableCell align="left" sx={{ py: 1.5, minWidth: 160 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isError || coursings.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 6, color: TEXT_SECONDARY, fontSize: 13 }}>
                    {isError ? "Error fetching data" : "No data available"}
                  </TableCell>
                </TableRow>
              ) : (
                coursings
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row: IProductTag, index) => (
                    <TableRow key={row.id}>
                      <TableCell className={classes.tablCell}>
                        <Checkbox
                          size="small"
                          checked={bulk.isSelected(row.id)}
                          onChange={() => bulk.toggleOne(row.id)}
                        />
                      </TableCell>
                      <TableCell scope="row" className={classes.tablCell}>
                        {page * rowsPerPage + index + 1}
                      </TableCell>
                      <TableCell component="th" scope="row" className={classes.tablCell} sx={{ fontWeight: 600, color: TEXT_PRIMARY }}>
                        {row.tagName}
                      </TableCell>
                      <TableCell scope="row" className={classes.tablCell}>
                        <Box
                          sx={{
                            width: 28,
                            height: 28,
                            borderRadius: "50%",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: row.isActive ? alpha(SUCCESS, 0.15) : alpha(RED, 0.15),
                          }}
                        >
                          {row.isActive ? (
                            <CheckIcon sx={{ color: SUCCESS, fontSize: 18 }} />
                          ) : (
                            <CloseIcon sx={{ color: RED, fontSize: 18 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="left" className={classes.tablCell} sx={{ minWidth: 160 }}>
                        <ActionButtons
                          onEdit={() => handleOpenDialog(row)}
                          onDelete={() => handleOpenDeletePopup(row)}
                        />
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${BORDER}`, display: "flex", justifyContent: "flex-end" }}>
          <Pagination
            count={coursings.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
          />
        </Box>
      </Box>

      {selectedCoursing && (
        <ProductTag
          openModel={openDialog}
          handleCloseDialog={handleCloseDialog}
          data={selectedCoursing}
        />
      )}
      {coursingToDelete && (
        <DeletePopup
          open={openDeleteCategory}
          handleCloseDelete={handleCloseDeletePopup}
          onConfirm={async () => {
            await handleDelete(coursingToDelete.id.toString());
          }}
          title="Delete Product Tag"
          content={`Are you sure you want to delete "${coursingToDelete.tagName}"?`}
        />
      )}
    </PageContainer>
  );
};

export default Index;
